import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { AndroidDevice, Doc, IosDevice, ModelClass, References } from '/imports/types';
import { userDisplayName } from '/imports/api/users';
import { validateExists, validateString } from '/imports/validators';
import { DeclareMethod } from '/imports/methods';
import { pluck } from '/imports/utils';
import { Orgs } from '/imports/api/orgs';
import { ErrorCode } from '/imports/errors';
import { strings } from '/imports/strings';

export interface Account extends Doc {
  _id: string;
  nid: number;
  active?: boolean;
  lastActiveState?: boolean;
  deleted?: boolean;
  orgId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  roles?: string[];
  androidDevices?: AndroidDevice[];
  iosDevices?: IosDevice[];
  _user?: { _id: string, username?: string, firstName?: string, lastName?: string, isSuperUser?: boolean };
  _org?: { _id: string, name?: string };
}

type Model = Account;
type Refs = References<Model>;

export const Accounts = new Mongo.Collection<Account>('accounts');

Meteor.isServer && Meteor.startup(async () => {
  await Accounts.createIndexAsync({ orgId: 1, userId: 1 }, { unique: true, sparse: true });
});

export const Roles = {
  admin: 'admin',
  generate: 'generate',
  productionMeters: 'productionMeters',
  consumptionMeters: 'consumptionMeters',
  prospector: 'prospector',  // the whole Prospector module, enrichment included — it spends money per prospect
  utilityAccounts: 'utilityAccounts',
  ricExecutive: 'ricExecutive',
  calpine: 'calpine',
  projectManagement: 'projectManagement',
  // Paired with admin + projectManagement: may approve, reject and un-decide PM document versions. On its
  // own it grants nothing — the approval gate is the whole trio.
  pmDocumentApprover: 'pmDocumentApprover',
  inventory: 'inventory',
  // The Documents module. Paired with admin it also administers the drive: root folders, any node's
  // permissions, and the whole tree regardless of per-folder grants.
  documents: 'documents',
  resigen: 'resigen',
  accounting: 'accounting', // paired with projectManagement: financial visibility (project Budget tab, pipeline Finances)
  // Accounting, plus the authority to approve an invoice. Everywhere `accounting` grants something this
  // grants the same (accountHasAccounting); the difference is the approval, which was super-user-only.
  invoiceApprover: 'invoiceApprover',
  luminia: 'luminia',       // Luminia's own people: inside the modules they can reach they get the super user's features (isLuminiaUser) — Generate: everything but moving a project between orgs; PM: excusing task requirements
};

/**
 * Whether an account carries accounting's grants. `invoiceApprover` IS accounting plus the authority to
 * approve, so every accounting check goes through here rather than naming the role directly — otherwise
 * each new accounting-gated feature has to remember the second role, and one day it will not.
 * @param account - the account to test
 */
export const accountHasAccounting = (account?: { roles?: string[] } | null): boolean =>
  !!account?.roles?.includes(Roles.accounting) || !!account?.roles?.includes(Roles.invoiceApprover);

/**
 * Validates an account's required and optional fields, including org/user existence.
 * @param account - account to validate
 * @returns map of field name to error messages
 */
export const validateAccount = async (account: Partial<Account>) => {
  const errors: { [key: string]: string[] } = {};
  validateString(errors, account, 'orgId', { required: true });
  account.orgId && await validateExists(errors, 'orgId', Orgs, account.orgId);
  validateString(errors, account, 'userId', { required: true });
  account.userId && await validateExists(errors, 'userId', Meteor.users, account.userId);
  validateString(errors, account, 'firstName', { required: true });
  validateString(errors, account, 'lastName', { required: true });
  validateString(errors, account, 'email');
  validateString(errors, account, 'phone');
  return errors;
};

const fields = [
  'nid', 'active', 'deleted', 'orgId', 'userId', 'firstName', 'lastName', 'email', 'phone', 'roles',
];

/**
 * Picks the persistable fields from an account.
 * @param account - raw account input
 * @param customFields - field list to use instead of the default
 * @returns account with only the allowed fields
 */
export const cleanAccount = (account: Partial<Account>, customFields?: string[]) => {
  const _fields = customFields || fields;
  return pluck(account, _fields);
};

type CreateAccountReturn = { accountId: string, email: string, createdUser?: boolean | { username?: string }, newRoles: string[] };

export const listAccountsForOrg = DeclareMethod<string | void, Account[]>('listAccountsForOrg');
export const getAccount = DeclareMethod<string, Account>('getAccount');
export const createAccount = DeclareMethod<Partial<Account>, CreateAccountReturn>('createAccount');
export const updateAccount = DeclareMethod<Partial<Account>, string>('updateAccount');
export const updateAccountProfile = DeclareMethod<Partial<Account>>('updateAccountProfile');
export const deleteAccount = DeclareMethod<string>('deleteAccount');
export const getAccountUser = DeclareMethod<string, Meteor.User>('getAccountUser');
export const getAccountLoginJwt = DeclareMethod<string, string>('getAccountLoginJwt');
export const accountUsername = DeclareMethod<string, string>('accountUsername');
interface UpdateAccountCredentials { accountId: string, credentials: { username?: string, password?: string } }
export const updateAccountCredentials = DeclareMethod<UpdateAccountCredentials>('updateAccountCredentials');
export const getAccountForUser = DeclareMethod<string, Account>('getAccountForUser');

// --- Invitations: an admin emails a signed link; the recipient fills a short form and lands signed in. ---
/** Emails an invitation into the caller's org. Roles are filtered server-side to those the caller holds. */
export const sendAccountInvitation = DeclareMethod<{ email: string, roles: string[] }, void>('sendAccountInvitation');
/** What an invitation token grants, for the accept page to show before the recipient commits. */
export const getAccountInvitation = DeclareMethod<string, { orgName: string, email: string, roles: string[] }>('getAccountInvitation');
/** Redeems an invitation: find-or-create the user by email, upsert their account in the org, and return a login token. */
export const acceptAccountInvitation = DeclareMethod<{ jwt: string, firstName: string, lastName: string, phone?: string }, { loginJwt: string }>('acceptAccountInvitation');

/** Display label for an account role. */
export const accountRoleLabel = (role: string): string => ({
  [Roles.admin]: strings.Admin(),
  [Roles.generate]: strings.Generate(),
  [Roles.productionMeters]: strings.RoleProductionMeters(),
  [Roles.consumptionMeters]: strings.RoleConsumptionMeters(),
  [Roles.prospector]: strings.RoleProspector(),
  [Roles.utilityAccounts]: strings.UtilityAccounts(),
  [Roles.ricExecutive]: strings.RoleRicExecutive(),
  [Roles.calpine]: strings.RoleCalpine(),
  [Roles.projectManagement]: strings.ProjectManagement(),
  [Roles.inventory]: strings.Inventory(),
  [Roles.documents]: strings.Documents(),
  [Roles.resigen]: strings.ResiGen(),
  [Roles.accounting]: strings.RoleAccounting(),
  [Roles.invoiceApprover]: strings.RoleInvoiceApprover(),
  [Roles.pmDocumentApprover]: strings.RolePmDocumentApprover(),
  [Roles.luminia]: strings.RoleLuminia(),
} as { [role: string]: string })[role] || role;

/**
 * Checks whether a user may administer accounts in an org (superuser or admin role).
 * @param user - the acting user
 * @param orgId - target org; defaults to the user's org
 * @returns true if allowed
 */
export const canAdminAccounts = async (user?: Meteor.User | null, orgId?: string) => {
  if (!user) return false;
  if (user?.isSuperUser) return true;
  orgId = orgId || user.orgId;
  if (!orgId) return false;
  const account = await Accounts.findOneAsync({ userId: user._id, orgId });
  if (account?.roles?.includes(Roles.admin)) return true;
  return false;
};

/**
 * Checks whether a user may list accounts in an org.
 * @param user - the acting user
 * @param orgId - target org; defaults to the user's org
 * @returns true if allowed
 */
export const canListAccounts = async (user?: Meteor.User | null, orgId?: string) => {
  return await canAdminAccounts(user, orgId);
};

/**
 * Builds a Mongo query matching accounts by id, name, email, or phone.
 * @param search - search text
 * @returns Mongo selector ({} when search is empty)
 */
export const accountsSearchQuery = (search?: string) => {
  search = search?.trim();
  if (!search?.length) return {};
  return {
    $or: [
      { _id: search },
      { firstName: new RegExp(search, 'gi') },
      { lastName: new RegExp(search, 'gi') },
      { email: new RegExp(search, 'gi') },
      { phone: new RegExp(search, 'gi') },
      { phone: new RegExp(search, 'gi') },
    ]
  };
};

/**
 * Resolves a human-readable name for an account, falling back to its user or id.
 * @param account - the account
 * @returns display name (empty string if no account)
 */
export const accountDisplayName = <T extends Pick<Account, '_id' | 'firstName' | 'lastName' | 'userId' | '_user'>>(account?: T) => {
  if (!account) return '';
  if (account.firstName && account.lastName) return `${account.firstName} ${account.lastName}`;
  const user = account._user;
  return user && userDisplayName(user) || account._id;
};

/**
 * Finds the account linking a user to a specific org.
 * @param user - the user
 * @param orgId - the org
 * @returns the matching account, or undefined
 */
export const accountForOrg = async (user?: Meteor.User | null, orgId?: string) => {
  if (user && orgId) return await Accounts.findOneAsync({ orgId, userId: user._id }) || undefined;
};

/**
 * Asserts (or checks) that the current user can administer the given org.
 * @param orgId - target org
 * @param throws - throw a forbidden error on failure instead of returning false
 * @returns the admin account or true if allowed, else false
 */
export const adminGuard = async (orgId?: string, throws?: boolean) => {
  const user = await Meteor.userAsync();
  if (!user || !orgId) throw new Meteor.Error(ErrorCode.forbidden);
  if (user?.isSuperUser) return true;
  if (user.orgId !== orgId) {
    if (throws) throw new Meteor.Error(ErrorCode.forbidden);
    return false;
  }
  const account = await Accounts.findOneAsync({ userId: user._id, orgId });
  if (account?.roles?.includes(Roles.admin)) return account;
  if (throws) throw new Meteor.Error(ErrorCode.forbidden);
  return false;
};

/**
 * Returns the account for the current (or given) user in their current org.
 * @param user - user to resolve; defaults to the logged-in user
 * @returns the account, or undefined
 */
export const currentAccount = async (user?: Meteor.User | null) => {
  user = user || await Meteor.userAsync();
  const userId = user?._id;
  const orgId = user?.orgId;
  if (!orgId || !userId) return;
  return await Accounts.findOneAsync({ orgId, userId });
};

/** Luminia's own people: a super user, or an account holding the luminia role. Inside a module they can reach
 *  they get the super user's features — everything except moving a project between orgs, which stays a
 *  super-user act. Client and server share this one predicate. */
export const isLuminiaUser = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  !!user?.isSuperUser || !!account?.roles?.includes(Roles.luminia);

/** Server: isLuminiaUser for the current (or given) user, with their account resolved in their current org. */
export const currentUserIsLuminia = async (user?: Meteor.User | null): Promise<boolean> => {
  user = user || await Meteor.userAsync();
  if (!user) return false;
  if (user.isSuperUser) return true;
  return isLuminiaUser(user, await currentAccount(user));
};

/** Asserts the current user is a super user or holds the luminia role in their current org. */
export const luminiaGuard = async () => {
  const user = await Meteor.userAsync();
  if (!user) throw new Meteor.Error(ErrorCode.unauthorized);
  if (!(await currentUserIsLuminia(user))) throw new Meteor.Error(ErrorCode.forbidden);
  return user;
};

/** Consumption-meter viewers: a super user, or an account holding the consumptionMeters role. The role is
 *  READ access — the meter, its utility bills and its intervals. Editing a meter, pulling intervals and
 *  managing interconnections stay super-user acts. Client and server share this one predicate. */
export const hasConsumptionMeters = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  !!user?.isSuperUser || !!account?.roles?.includes(Roles.consumptionMeters);

/** Server: hasConsumptionMeters for the current (or given) user, with their account resolved in their current org. */
export const currentUserHasConsumptionMeters = async (user?: Meteor.User | null): Promise<boolean> => {
  user = user || await Meteor.userAsync();
  if (!user) return false;
  if (user.isSuperUser) return true;
  return hasConsumptionMeters(user, await currentAccount(user));
};

/** Asserts the current user is a super user or holds the consumptionMeters role in their current org. */
export const consumptionMetersGuard = async () => {
  const user = await Meteor.userAsync();
  if (!user) throw new Meteor.Error(ErrorCode.unauthorized);
  if (!(await currentUserHasConsumptionMeters(user))) throw new Meteor.Error(ErrorCode.forbidden);
  return user;
};

/** Generate users: a super user or Luminia's own people, an account holding `generate`, or an org ADMIN.
 *  Admin alone has always carried a whole-org project read — in `listProjectsScope` it widens the scope
 *  rather than opening the door — so the page must let it in or it refuses someone the server serves. */
export const hasGenerate = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  isLuminiaUser(user, account) || !!account?.roles?.includes(Roles.generate) || !!account?.roles?.includes(Roles.admin);

/** Production-data users: a super user, or an account holding the productionMeters role. Read-only, like
 *  consumptionMeters: creating or editing a meter stays a super-user act. Client and server share this one. */
export const hasProductionMeters = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  !!user?.isSuperUser || !!account?.roles?.includes(Roles.productionMeters);

/** Server: hasProductionMeters for the current (or given) user, with their account resolved in their current org. */
export const currentUserHasProductionMeters = async (user?: Meteor.User | null): Promise<boolean> => {
  user = user || await Meteor.userAsync();
  if (!user) return false;
  if (user.isSuperUser) return true;
  return hasProductionMeters(user, await currentAccount(user));
};

/** Asserts the current user is a super user or holds the productionMeters role in their current org. */
export const productionMetersGuard = async () => {
  const user = await Meteor.userAsync();
  if (!user) throw new Meteor.Error(ErrorCode.unauthorized);
  if (!(await currentUserHasProductionMeters(user))) throw new Meteor.Error(ErrorCode.forbidden);
  return user;
};

/** Utility-account users: a super user, or an account holding the utilityAccounts role. These records carry
 *  the stored utility CREDENTIAL, so this is the narrowest of the three data-hub roles. */
export const hasUtilityAccounts = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  !!user?.isSuperUser || !!account?.roles?.includes(Roles.utilityAccounts);

/** Server: hasUtilityAccounts for the current (or given) user, with their account resolved in their current org. */
export const currentUserHasUtilityAccounts = async (user?: Meteor.User | null): Promise<boolean> => {
  user = user || await Meteor.userAsync();
  if (!user) return false;
  if (user.isSuperUser) return true;
  return hasUtilityAccounts(user, await currentAccount(user));
};

/** Asserts the current user is a super user or holds the utilityAccounts role in their current org. */
export const utilityAccountsGuard = async () => {
  const user = await Meteor.userAsync();
  if (!user) throw new Meteor.Error(ErrorCode.unauthorized);
  if (!(await currentUserHasUtilityAccounts(user))) throw new Meteor.Error(ErrorCode.forbidden);
  return user;
};

/** Prospector users: a super user, or an account holding the prospector role. Unlike consumptionMeters this
 *  is NOT read-only — it carries the whole module, enrichment included, which spends money with Google,
 *  Reonomy and the model providers per prospect. Everything it reaches is still scoped to the holder's own
 *  org. Client and server share this one predicate. */
export const hasProspector = (user?: { isSuperUser?: boolean } | null, account?: { roles?: string[] } | null): boolean =>
  !!user?.isSuperUser || !!account?.roles?.includes(Roles.prospector);

/** Server: hasProspector for the current (or given) user, with their account resolved in their current org. */
export const currentUserHasProspector = async (user?: Meteor.User | null): Promise<boolean> => {
  user = user || await Meteor.userAsync();
  if (!user) return false;
  if (user.isSuperUser) return true;
  return hasProspector(user, await currentAccount(user));
};

/** Asserts the current user is a super user or holds the prospector role in their current org. */
export const prospectorGuard = async () => {
  const user = await Meteor.userAsync();
  if (!user) throw new Meteor.Error(ErrorCode.unauthorized);
  if (!(await currentUserHasProspector(user))) throw new Meteor.Error(ErrorCode.forbidden);
  return user;
};

export const modelClass: ModelClass<Model, Refs> = {
  collection: Accounts,
  fieldLimits: {
    '_id': 64, 'createdByName': 256, 'createdByUserId': 64, 'email': 320, 'firstName': 256, 'lastName': 256, 'name': 256,
    'orgId': 64, 'phone': 64, 'roles': 64, 'updatedByName': 256, 'updatedByUserId': 64, 'userId': 64,
  },
  editableFields: fields,
  listMethod: () => [] as any,
  getMethod: () => null as any,
  createMethod: () => '' as any,
  updateMethod: updateAccount,
  deleteMethod: deleteAccount,
  pathPrefix: '/users',
  asString: (document) => accountDisplayName(document),
  modelName: strings.Account(),
  pluralName: strings.Accounts(),
  searchQuery: accountsSearchQuery,
  validate: validateAccount,
};